/**
 * BrioDropdown -- focus management, keyboard navigation, scroll-close, and
 * trigger ARIA wiring for popover-based action menus.
 *
 * The Popover API handles show/hide, Escape, and light dismiss (click
 * outside). This custom element adds the behaviors the platform doesn't:
 *
 *   1. Arrow keys / Home / End across menu items, with wrap. Provided by
 *      BrioBase.keyNav, which implements the roving-tabindex pattern.
 *
 *   2. Focus the [aria-current="true"] item if one exists, otherwise the
 *      first enabled menu item, when the menu opens. Return focus to the
 *      trigger when it closes. Matches native menu behavior on macOS and
 *      Windows -- combo-box and selection menus open with the current
 *      selection highlighted so arrow keys navigate from there.
 *
 *   3. Close on item click. Standard menu UX: clicking a menuitem
 *      dismisses the menu so the action's effect is visible. The
 *      consumer's own click handler on the item still fires first
 *      (it's attached deeper in the DOM, bubble phase reaches it
 *      before our menu-level listener).
 *
 *   4. Close on scroll. Popovers live in the top layer and visually detach
 *      from triggers inside sticky/fixed ancestors when the page scrolls --
 *      anchor positioning recomputes on layout changes but not on scroll.
 *      Closing is the conventional menu UX (Mac Finder, Windows Explorer,
 *      Chrome's own menus). Capture phase catches scroll on any ancestor.
 *
 *   5. Trigger ARIA wiring. The Popover API does NOT set aria-expanded or
 *      aria-haspopup on the trigger automatically -- those are semantics
 *      concerns separate from the show/hide mechanism. This element owns
 *      both attributes for the lifetime of the dropdown.
 *
 * Expected markup:
 *   <brio-dropdown>
 *       <button class="button" type="button"
 *               commandfor="menu-id" command="toggle-popover">Actions</button>
 *
 *       <div class="popover dropdown" id="menu-id" popover role="menu">
 *           <button class="dropdown-item" role="menuitem">Edit</button>
 *           <hr role="separator">
 *           <button class="dropdown-item is-danger"
 *                   role="menuitem">Delete</button>
 *       </div>
 *   </brio-dropdown>
 *
 * Why commandfor + command, not popovertarget?
 *
 * brio uses commandfor / command as the unified invoker pattern across
 * every overlay (dialog needs it, since show-modal has no popovertarget
 * equivalent; popover and dropdown follow for consistency). The polyfill
 * in command.js fills the gap on browsers without native support.
 *
 * Combining popovertarget AND command="show-popover" on the same trigger
 * fires *both* invokers per click -- one toggles, the other unconditionally
 * shows. Race conditions across browsers produced stuck-open popovers in
 * Safari and intermittent stuck-state in Chrome. Pick one mechanism per
 * trigger; brio picks commandfor.
 *
 * Disabled items: prefer :disabled on <button role="menuitem">; use
 * [aria-disabled="true"] on <a role="menuitem"> where :disabled doesn't
 * apply. The keyboard navigation skips both forms.
 */

import { BrioBase } from './brio-base.js';

// Excludes disabled items so arrow keys skip them, matching native menus.
const ITEM_SELECTOR = '[role="menuitem"]:not(:disabled):not([aria-disabled="true"])';

class BrioDropdown extends BrioBase {
    static tag = 'brio-dropdown';
    static {
        customElements.define(BrioDropdown.tag, BrioDropdown);
    }

    // Private class fields -- enforced at the language level so external
    // code can't reach in and manipulate internal state. Listeners are
    // stored on the instance so disconnectedCallback can remove the exact
    // function references it added.
    #menu;
    #trigger;
    #onToggle;
    #onScroll;
    #onClick;
    #cleanupNav;

    connectedCallback() {
        this.#menu = this.$('[role="menu"]');
        // Find the trigger by commandfor -- brio's invoker pattern across
        // every overlay (dialog, popover, dropdown). Looking for [commandfor]
        // alone (rather than also [popovertarget]) is deliberate: combining
        // both attributes on a single trigger fires two invocations per
        // click, which produced stuck-open popovers across browsers.
        this.#trigger = this.$('[commandfor]');
        if (!this.#menu || !this.#trigger) return;

        // Trigger ARIA: haspopup is static for the dropdown's lifetime;
        // aria-expanded toggles with the popover state. Set both unconditionally
        // -- brio-dropdown owns these attributes once it connects.
        this.#trigger.setAttribute('aria-haspopup', 'menu');
        this.#trigger.setAttribute('aria-expanded', 'false');

        // BrioBase.keyNav wires ArrowUp/Down + Home/End with circular wrap,
        // returning a cleanup function we call on disconnect.
        this.#cleanupNav = this.keyNav(ITEM_SELECTOR, { vertical: true });

        // Item click closes the menu. Standard menu UX: clicking a
        // menuitem dismisses the menu so the action is visible. The
        // listener is on the menu element (not on `this`) so trigger
        // clicks -- which open the menu in the first place -- can't
        // accidentally fire this. closest('[role="menuitem"]') walks up
        // from the actual click target, so clicks on a menuitem's
        // children (leading <svg>, trailing <kbd>, etc.) still resolve
        // to the menuitem and close. Disabled items have pointer-events:
        // none in CSS, so click events never reach them -- no
        // disabled-item guard needed here.
        //
        // We don't preventDefault: the consumer's own click handler on
        // the item still fires (it's attached deeper in the DOM, bubble
        // phase reaches it before this menu-level listener), and any
        // default behavior (e.g. <a href> navigation on link items)
        // also runs. We just close.
        this.#onClick = (e) => {
            const item = e.target.closest('[role="menuitem"]');
            if (!item) return;
            this.#menu.hidePopover();
        };
        this.#menu.addEventListener('click', this.#onClick);

        // The Popover API's toggle event doesn't bubble; listen on this
        // host element with capture: true to catch it during the capture
        // phase. e.newState is "open" or "closed".
        this.#onToggle = (e) => {
            if (e.target !== this.#menu) return;

            if (e.newState === 'open') {
                this.#trigger.setAttribute('aria-expanded', 'true');

                // requestAnimationFrame ensures the popover has been promoted
                // to the top layer and is focusable before we move focus.
                // Prefer [aria-current="true"] (the current selection in a
                // selection menu) over the first item; falls back to first
                // for action menus where no item is "current."
                requestAnimationFrame(() => {
                    const items = this.$$(ITEM_SELECTOR);
                    if (!items.length) return;
                    const current = this.#menu.querySelector(
                        `${ITEM_SELECTOR}[aria-current="true"]`
                    );
                    (current || items[0]).focus();
                });

                // Close on scroll. Passive because we don't preventDefault;
                // capture so we catch scroll on any ancestor (not just
                // window). Scrolling inside the menu itself is excluded --
                // a menu with its own overflow shouldn't dismiss itself
                // mid-scroll.
                this.#onScroll = (e) => {
                    if (this.#menu.contains(e.target)) return;
                    this.#menu.hidePopover();
                };
                window.addEventListener('scroll', this.#onScroll, {
                    passive: true,
                    capture: true,
                });
            } else if (e.newState === 'closed') {
                this.#trigger.setAttribute('aria-expanded', 'false');

                if (this.#onScroll) {
                    window.removeEventListener('scroll', this.#onScroll, {
                        capture: true,
                    });
                    this.#onScroll = null;
                }

                this.#trigger.focus();
            }
        };
        this.addEventListener('toggle', this.#onToggle, true);
    }

    disconnectedCallback() {
        this.#cleanupNav?.();
        if (this.#onClick && this.#menu) {
            this.#menu.removeEventListener('click', this.#onClick);
        }
        if (this.#onScroll) {
            window.removeEventListener('scroll', this.#onScroll, { capture: true });
        }
        if (this.#onToggle) {
            this.removeEventListener('toggle', this.#onToggle, true);
        }
    }
}
